import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {login, loginWithGoogleCredential} from "../utils/userSlice";
import {AppDispatch} from "../utils/store";
import {useDispatch} from "react-redux";
import SportaLogo from "../assets/images/sporta-logo.png";
import {Box, Button, IconButton, InputBase, Typography} from '@mui/material';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {GoogleLogin, GoogleOAuthProvider, CredentialResponse} from '@react-oauth/google';
import config from "../config/config";
import {isAccountFrozenFlagged} from "../services/accountFrozen";

/** Shell around each input: a flex row so the password toggle can sit at the
 *  logical inline-end in BOTH RTL and LTR (never absolute LTR coordinates). */
const fieldShellSx = (hasError: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    height: '52px',
    borderRadius: '13px',
    backgroundColor: '#071826',
    border: '1.5px solid',
    borderColor: hasError ? 'error.main' : 'rgba(255,255,255,0.14)',
    px: 1.75,
    transition: 'border-color .15s ease',
    '&:focus-within': {
        borderColor: hasError ? 'error.main' : '#2ea6e6',
    },
});

const inputSx = {
    flexGrow: 1,
    color: '#fff',
    fontSize: '16px',
    '& .MuiInputBase-input': {
        padding: 0,
        height: '100%',
        '&::placeholder': {color: '#5c7a90', opacity: 1},
    },
};

const Login: React.FC = () => {
    const {t, i18n} = useTranslation();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const [googleBtnWidth, setGoogleBtnWidth] = useState<string | undefined>(undefined);
    // Set when the API rejected this account as frozen (mid-session logout or a
    // login attempt); cleared by the next successful login.
    const [accountFrozen, setAccountFrozen] = useState(isAccountFrozenFlagged);

    const validationSchema = yup.object().shape({
        email: yup.string()
            .email(t('login.validation.emailValid'))
            .required(t('login.validation.emailRequired')),
        password: yup.string()
            .required(t('login.validation.passwordRequired')),
    });

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                const success = await dispatch(await login(values.email, values.password));
                if (success) {
                    const redirectPath = location.state?.from?.pathname || '/';
                    navigate(redirectPath);
                } else {
                    setAccountFrozen(isAccountFrozenFlagged());
                }
            } catch (error) {
                toast.error(t('login.notification.loginFailed'));
            }
        },
        validateOnBlur: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Match the Google button width to the form (GIS clamps to 200–400px).
    useEffect(() => {
        if (!config.googleClientId) return;
        const el = formRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const update = () => {
            const w = Math.round(el.getBoundingClientRect().width);
            setGoogleBtnWidth(String(Math.min(400, Math.max(200, w))));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        const credential = credentialResponse.credential;
        if (!credential) {
            toast.error(t('login.notification.googleFailed'));
            return;
        }
        const success = await dispatch(loginWithGoogleCredential(credential));
        if (success) {
            const redirectPath = location.state?.from?.pathname || '/';
            navigate(redirectPath);
        } else {
            setAccountFrozen(isAccountFrozenFlagged());
        }
    };

    const handleGoogleError = () => {
        toast.error(t('login.notification.googleFailed'));
    };

    const emailError = Boolean(formik.touched.email && formik.errors.email);
    const passwordError = Boolean(formik.touched.password && formik.errors.password);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                background: 'radial-gradient(120% 60% at 50% 0%, #0d2a42, #071726 62%, #050f1c)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                py: 4,
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: {xs: '100%', sm: '420px'},
                    px: {xs: 0, sm: 4},
                    py: {xs: 0, sm: 4},
                    borderRadius: {xs: 0, sm: '20px'},
                    border: {xs: 'none', sm: '1px solid rgba(255,255,255,0.12)'},
                    backgroundColor: {xs: 'transparent', sm: 'rgba(5,16,29,0.6)'},
                    boxSizing: 'border-box',
                }}
            >
                <Box sx={{textAlign: 'center', mb: 4}}>
                    <img src={SportaLogo} alt="Logo" style={{height: 40}}/>
                    <Typography
                        component="h1"
                        sx={{color: '#fff', fontWeight: 700, fontSize: '1.6rem', mt: 3}}
                    >
                        {t('login.title')}
                    </Typography>
                    <Typography sx={{color: '#8fb0c8', fontSize: '0.95rem', mt: 0.75}}>
                        {t('login.subtitle')}
                    </Typography>
                </Box>

                {accountFrozen && (
                    <Box role="alert" data-testid="account-frozen" sx={{
                        mb: 3,
                        px: 2,
                        py: 1.5,
                        borderRadius: '13px',
                        border: '1px solid rgba(255,107,107,0.45)',
                        backgroundColor: 'rgba(255,107,107,0.10)',
                        color: '#ffb3b3',
                        fontSize: '0.9rem',
                        textAlign: 'center',
                    }}>
                        {t('login.notification.accountFrozen')}
                    </Box>
                )}

                <form ref={formRef} noValidate onSubmit={formik.handleSubmit}>
                    <Box sx={{mb: 2.5}}>
                        <Typography
                            component="label"
                            htmlFor="email"
                            sx={{display: 'block', color: '#8fb0c8', fontSize: '0.8rem', fontWeight: 600, mb: 0.75}}
                        >
                            {t('login.form.emailLabel')}
                        </Typography>
                        <Box sx={fieldShellSx(emailError)}>
                            <InputBase
                                id="email"
                                name="email"
                                type="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder={t('login.form.emailPlaceholder')}
                                inputProps={{dir: 'auto'}}
                                sx={inputSx}
                            />
                        </Box>
                        {emailError && (
                            <Typography sx={{color: 'error.main', fontSize: '0.75rem', mt: 0.5}}>
                                {formik.errors.email}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{mb: 3}}>
                        <Typography
                            component="label"
                            htmlFor="password"
                            sx={{display: 'block', color: '#8fb0c8', fontSize: '0.8rem', fontWeight: 600, mb: 0.75}}
                        >
                            {t('login.form.passwordLabel')}
                        </Typography>
                        <Box sx={fieldShellSx(passwordError)}>
                            <InputBase
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder={t('login.form.passwordPlaceholder')}
                                inputProps={{dir: 'auto'}}
                                sx={inputSx}
                            />
                            <IconButton
                                onClick={handleTogglePasswordVisibility}
                                tabIndex={-1}
                                aria-label="toggle password visibility"
                                sx={{color: '#8fb0c8', p: 0.75}}
                            >
                                {showPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                            </IconButton>
                        </Box>
                        {passwordError && (
                            <Typography sx={{color: 'error.main', fontSize: '0.75rem', mt: 0.5}}>
                                {formik.errors.password}
                            </Typography>
                        )}
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        disabled={formik.isSubmitting}
                        sx={{
                            height: '52px',
                            borderRadius: '14px',
                            backgroundColor: '#17c47f',
                            color: '#04241a',
                            fontSize: '1rem',
                            fontWeight: 800,
                            textTransform: 'none',
                            '&:hover': {backgroundColor: '#13a86c'},
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(23,196,127,0.5)',
                                color: 'rgba(4,36,26,0.7)',
                            },
                        }}
                    >
                        {t('login.form.submitButton')}
                    </Button>
                </form>

                {config.googleClientId && (
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5}}>
                            <Box sx={{flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.15)'}}/>
                            <Typography sx={{color: '#8fb0c8', fontSize: '0.8rem'}}>
                                {t('login.form.or')}
                            </Typography>
                            <Box sx={{flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.15)'}}/>
                        </Box>

                        <Box sx={{display: 'flex', justifyContent: 'center'}}>
                            <GoogleOAuthProvider
                                clientId={config.googleClientId}
                                locale={i18n.language?.startsWith('he') ? 'he' : 'en'}
                            >
                                <GoogleLogin
                                    theme="filled_black"
                                    shape="pill"
                                    width={googleBtnWidth}
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                />
                            </GoogleOAuthProvider>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Login;
